import React, { useState, useRef, useEffect } from 'react';
import { Input } from 'antd';
import SpecDrawer from '../SpecDrawer/SpecDrawer';
import AccentButton from '../AccentButton/AccentButton';
import EditingBar from '../EditingBar/EditingBar';
import { DefaultAvatar, ChatIcon } from '../icons';
import { containsProfanity } from '../../utils/profanityFilter';
import { useIsMobile } from '../../hooks/useIsMobile';
import './AddCommentDrawer.css';

export interface ShowContext {
  bandName: string;
  venueName: string;
  date: string;
  imageUrl?: string;
}

export interface AddCommentDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (comment: { text: string; userName?: string }) => void;
  showContext: ShowContext;
  isLoggedIn?: boolean;
  currentUser?: { name: string; avatarUrl?: string };
  /** Pre-fill the text input (e.g. from a quick emoji tap) */
  initialText?: string;
}

export const AddCommentDrawer: React.FC<AddCommentDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  showContext,
  isLoggedIn = false,
  currentUser,
  initialText = '',
}) => {
  const isMobile = useIsMobile();
  const [text, setText] = useState('');
  const [guestName, setGuestName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [flashComment, setFlashComment] = useState(false);
  const [flashName, setFlashName] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setText(initialText);
      setGuestName('');
      setNameError(null);
      setFlashComment(false);
      setFlashName(false);
      setTimeout(() => textareaRef.current?.focus(), 300);
    }
  }, [open, initialText]);

  const handleNameChange = (val: string) => {
    setGuestName(val);
    if (val.trim() && containsProfanity(val)) {
      setNameError('Name contains inappropriate language');
    } else {
      setNameError(null);
    }
  };

  const handleSubmit = () => {
    const missingComment = text.trim().length === 0;
    const missingName = !isLoggedIn && guestName.trim().length === 0;

    if (missingComment || missingName || nameError) {
      if (missingComment) {
        setFlashComment(true);
        setTimeout(() => setFlashComment(false), 1000);
      }
      if (missingName) {
        setFlashName(true);
        setTimeout(() => setFlashName(false), 1000);
      }
      if (missingComment) {
        textareaRef.current?.focus();
      } else if (missingName) {
        nameInputRef.current?.focus();
      }
      return;
    }

    onSubmit({
      text: text.trim(),
      userName: isLoggedIn ? undefined : guestName.trim(),
    });
    setText('');
    setGuestName('');
  };

  const drawerTitle = (
    <div className="add-comment-drawer__header">
      <div className="add-comment-drawer__drag-handle" />
      <EditingBar
        icon={<ChatIcon size="m" tone="white" />}
        title="Adding Comment"
        onCancel={onClose}
      />
    </div>
  );

  return (
    <SpecDrawer
      title={drawerTitle}
      open={open}
      onClose={onClose}
      size={isMobile ? 'large' : 'default'}
      swipeable={false}
      closable={false}
      zIndex={1200}
    >
      <div className="add-comment-drawer__body">
        <div className="add-comment-drawer__field">
          <label className="add-comment-drawer__label">Comment</label>
          <Input.TextArea
            ref={textareaRef as any}
            placeholder="Enter comment"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            autoSize={{ minRows: 3, maxRows: 8 }}
            className={`add-comment-drawer__textarea${flashComment ? ' add-comment-drawer__flash' : ''}`}
          />
        </div>

        {!isLoggedIn && (
          <div className="add-comment-drawer__field">
            <label className="add-comment-drawer__label">Name</label>
            <Input
              ref={nameInputRef as any}
              placeholder="Enter Name"
              value={guestName}
              onChange={(e) => handleNameChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); } }}
              maxLength={40}
              status={nameError ? 'error' : undefined}
              className={flashName ? 'add-comment-drawer__flash' : undefined}
            />
            {nameError && (
              <span className="add-comment-drawer__error">{nameError}</span>
            )}
          </div>
        )}

        {isLoggedIn && currentUser && (
          <div className="add-comment-drawer__field">
            <label className="add-comment-drawer__label">Posting as</label>
            <div className="add-comment-drawer__user-preview">
              <div className="add-comment-drawer__avatar">
                {currentUser.avatarUrl ? (
                  <img src={currentUser.avatarUrl} alt={currentUser.name} />
                ) : (
                  <DefaultAvatar size={32} />
                )}
              </div>
              <span className="add-comment-drawer__user-name">{currentUser.name}</span>
            </div>
          </div>
        )}

        <div className="add-comment-drawer__submit">
          <AccentButton
            size="large"
            shape="round"
            onClick={handleSubmit}
            block
          >
            Post Now
          </AccentButton>
        </div>
      </div>
    </SpecDrawer>
  );
};

export default AddCommentDrawer;
